import React, {  } from "react";
import InvoiceDTO from "../dtos/InvoiceDTO";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { Invoices_Get, Invoices_QrPaymentGet } from "data/endpoints";
import { InvoiceSubType } from "../enums/invoiceSubType";
import AdvancesSection from "./AdvancesSection";
import InvoiceLinesProductSection from "./InvoiceLinesProductSection";
import InvoiceLinesCalculationSection from "./InvoiceLinesCalculationSection";
import InvoiceBasicInfoCard from "../components/InvoiceBasicInfoCard";
import InvoiceRecapitulationCard from "../components/InvoiceRecapitulationCard";
import InvoicePaymentCard from "../components/InvoicePaymentCard";
import QRPaymentDTO from "features/payments/dtos/QRPaymentDTO";
import { useQuery } from "data/hooks/useQuery";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { isEmpty } from "features/shared/helpers/objects";
import { useAutoSpotTableDownload } from "features/invoices/hooks/useAutoSpotTableDownload";
import { getQueryParams } from "features/shared/helpers/queryParams";
import DocumentDownloadCard from "features/shared/components/documents/DocumentDownloadCard";
import useInvoiceDownloadTitle from "features/invoices/hooks/useInvoiceDownloadTitle";
import { useTranslation } from "react-i18next";

interface Props {
  invoiceId: string;
  proformaInvoiceId?: string;
}

const InvoiceDetailSection = ({ invoiceId, proformaInvoiceId }: Props) => {
  // translations
  const { i18n } = useTranslation();
  
  // query
  const isInvoiceId = (): boolean => {
    if (invoiceId) return true;

    return false;
  };

  const { data: invoice, loadStatus: invoiceStatus } = useQuery<InvoiceDTO>(
    {
      url: Invoices_Get,
      routeParams: getRouteParams("invoiceId", invoiceId),
      params: proformaInvoiceId ? getQueryParams("ProformaInvoiceId", proformaInvoiceId) : undefined,
    },
    {},
    [invoiceId, i18n.language],
    undefined,
    {
      apply: true,
      condition: isInvoiceId,
    }
  );

  const { data: qrPayment, loadStatus: qrPaymentStatus } =
    useQuery<QRPaymentDTO>(
      {
        url: Invoices_QrPaymentGet,
        routeParams: getRouteParams("invoiceId", invoiceId),
      },
      {},
      [invoiceId],
      undefined,
      {
        apply: true,
        condition: isInvoiceId,
      }
    );

  // automatic download spot table (if meet query params)
  useAutoSpotTableDownload();

  // hooks
  const formatDocumentTitle = useInvoiceDownloadTitle();

  // sub-renders
  const renderGridSections = (subType?: InvoiceSubType) => {
    let result: JSX.Element[] = [];

    switch (subType) {
      case InvoiceSubType.Invoice:
      case InvoiceSubType.CorrectedInvoice:
      case InvoiceSubType.ProductionInvoice:
        case InvoiceSubType.CorrectedProductionInvoice:
        result.push(
          <InvoiceLinesCalculationSection invoice={invoice} key="1" />
        );
        result.push(<AdvancesSection invoice={invoice} key="2" />);
        break;
      case InvoiceSubType.GeneralInvoice:
      case InvoiceSubType.CorrectedGeneralInvoice:
        result.push(<InvoiceLinesProductSection invoice={invoice} key="1" />);
        result.push(<AdvancesSection invoice={invoice} key="2" />);
        break;
      case InvoiceSubType.ProformaInvoice:
        result.push(
          <AdvancesSection
            invoice={invoice}
            proformaInvoiceId={proformaInvoiceId}
            key="1"
          />
        );
        break;
      default:
        break;
    }

    return result;
  };

  // render
  return (
    <NoContentWrapper
      data={invoice}
      loadStatus={[invoiceStatus, qrPaymentStatus]}
      isEmpty={() => isEmpty(invoice)}
    >
      <div className="row no-gutters mx-n2">
        <div className="col-xl-8 px-2">
          <InvoiceBasicInfoCard invoice={invoice} />
        </div>
        <div className="col-xl-4 px-2">
          <DocumentDownloadCard
            documents={invoice.documents?.length ? invoice.documents : []}
            exports={invoice.availableExports?.length ? invoice.availableExports : []}
            formatDocumentTitle={formatDocumentTitle}
          />
        </div>
      </div>
      <div className="row no-gutters mx-n2">
        <div className="col-xl-6 px-2">
          <InvoicePaymentCard invoice={invoice} qrPayment={qrPayment} />
        </div>

        <div className="col-xl-6 px-2">
          <InvoiceRecapitulationCard
            recapitulation={invoice.invoiceRecapitulation!}
            currency={invoice.currency!}
            type={invoice.invoiceType!}
          />
        </div>
      </div>
      {renderGridSections(invoice.invoiceSubType)}
    </NoContentWrapper>
  );
};

export default InvoiceDetailSection;